<template>
   <q-page padding>
        <div class="q-display-1 q-mb-md text-secondary">
            <span>
                <q-icon name="far fa-calendar-alt" class="q-mb-sm" /> {{ $t("events") }}

                <q-tooltip anchor="bottom right" self="top left">
                    {{ $t('news_help') }}
                </q-tooltip>
            </span>
        </div>

        <q-table
            :data="events"
            :columns="columns"
            :pagination.sync="server_pagination"
            @request="request"
            :loading="loading"
            :filter="filter"
            row-key="name"
            :no-data-label="$t('no_data_avaliable')"
            :no-results-label="$t('no_results_found')"
            :loading-label="$t('loading_data')"
            :rows-per-page-label="$t('items_per_page')"
            :rows-per-page-options="[5,10,50,100]"
            binary-state-sort
            >

            <template slot="top" slot-scope="props">
                <q-search v-model="filter" icon="far fa-search" :placeholder="$t('search')" :debounce="500" />
                <div class="col" />
                <q-btn color="secondary" icon="far fa-plus" :label="$t('new')" @click="$router.push({ name: 'event', params: { id: 0 } })" />
            </template>

            <q-tr slot="header" slot-scope="props" :props="props">
                <q-th v-for="col in props.cols" :key="col.name" :props="props">
                {{ $t(col.label) }}
                </q-th>
            </q-tr>

            <q-tr slot="body" slot-scope="props" :props="props">
                <q-td auto-width key="id" :props="props">
                    {{ props.row.id }}
                </q-td>

                <q-td key="title" :props="props">
                    {{ props.row.title }}
                </q-td>

                <q-td key="reward_campaign" :props="props">
                    {{ getCampaign(props.row.reward_campaign) }}
                </q-td>

                <q-td key="permission" :props="props">
                    <q-btn color="primary" text-color="white" :label="getQuantity(props.row.permission)" no-ripple>
                        <q-tooltip>
                            <p v-if="props.row.permission.areas.length > 0" class="tooltip q-mb-sm">
                                {{ $t('areas') }}:
                                <span v-for="(area, index) in props.row.permission.areas" :key="index">
                                    {{ getArea(area) }}
                                    <span v-if="(props.row.permission.areas.length - 1) > index">, </span>
                                </span>
                            </p>

                            <p v-if="props.row.permission.profiles.length > 0" class="tooltip q-mb-sm">
                                {{ $t('profiles') }}:
                                <span v-for="(profile, index) in props.row.permission.profiles" :key="index">
                                    {{ getUserProfile(profile) }}
                                    <span v-if="(props.row.permission.profiles.length - 1) > index">, </span>
                                </span>
                            </p>

                            <p v-if="props.row.permission.reward_levels.length > 0" class="tooltip q-mb-sm">
                                {{ $t('reward_levels') }}:
                                <span v-for="(value, index) in props.row.permission.reward_levels" :key="index">
                                    {{ getRewardLevels(value) }}
                                    <span v-if="(props.row.permission.reward_levels.length - 1) > index">, </span>
                                </span>
                            </p>
                        </q-tooltip>
                    </q-btn>
                </q-td>

                <q-td key="start_date" :props="props">
                    {{ props.row.start_date }}
                </q-td>

                <q-td key="end_date" :props="props">
                    {{ props.row.end_date }}
                </q-td>

                <q-td key="timezone" :props="props">
                    {{ props.row.timezone }}
                </q-td>

                <q-td key="status" :props="props" auto-width>
                    <q-chip small :icon="getIconStatus(props.row.status)" :color="getColorStatus(props.row.status)" class="full-width">
                        {{ $t(getStatus(props.row.status)) }}
                    </q-chip>
                </q-td>

                <q-td key="actions" :props="props">
                    <q-btn flat round color="primary" icon="far fa-pencil-alt" @click="$router.push({ name: 'event', params: { id: props.row.actions } })" />
                    <q-btn flat round color="negative" icon="far fa-trash-alt" @click="confirmRemove(props.row.actions)" />
                </q-td>
            </q-tr>
        </q-table>

        <q-dialog v-model="confirm_remove" @ok="removeItem">
            <span slot="title">{{ $t('remove_item') }}</span>

            <span slot="message">{{ $t('confirm_remove_item') }}</span>

            <template slot="buttons" slot-scope="props">
                <q-btn color="primary" :label="$t('cancel')" @click="confirm_remove = false" />
                <q-btn color="negative" :label="$t('remove')" @click="removeItem()" />
            </template>
        </q-dialog>

    </q-page>
</template>

<script>
import { mapGetters } from 'vuex'
import { date } from 'quasar'
import * as moment from 'moment'
import * as tz from 'moment-timezone'

export default {
    i18nOptions: { namespaces: [ 'custom', 'common', 'news', 'admin', 'events'] },
    data() {
        return {
            confirm_remove: false,
            action_id: '',
            loading: false,
            filter: '',
            events: [],
            server_pagination: {
                page: 1,
                rowsNumber: 10,
                rowsPerPage: null,
                descending: false,
                sortBy: null
            },
            columns: [
                {
                    name: 'id',
                    required: true,
                    label: '#',
                    align: 'left',
                    field: 'name',
                    sortable: true,
                },
                {
                    name: 'title',
                    required: true,
                    label: 'title',
                    align: 'left',
                    field: 'title',
                    sortable: true,
                },
                {
                    name: 'reward_campaign',
                    required: true,
                    label: 'campaign',
                    align: 'left',
                    field: 'reward_campaign',
                    sortable: true,
                },
                {
                    name: 'permission',
                    required: true,
                    label: 'permission',
                    align: 'left',
                    field: 'permission',
                    sortable: false,
                },
                {
                    name: 'start_date',
                    required: true,
                    label: 'start_date',
                    align: 'left',
                    field: 'start_date',
                    sortable: true,
                },
                {
                    name: 'end_date',
                    required: true,
                    label: 'end_date',
                    align: 'left',
                    field: 'end_date',
                    sortable: true,
                },
                {
                    name: 'timezone',
                    required: true,
                    label: 'timezone',
                    align: 'left',
                    field: 'timezone',
                    sortable: false,
                },
                {
                    name: 'status',
                    required: true,
                    label: 'status',
                    align: 'left',
                    field: 'status',
                    sortable: true,
                },
                {
                    name: 'actions',
                    required: true,
                    label: '',
                    align: 'right',
                    field: 'edit',
                    sortable: false,
                },
            ],
        }
    },

    computed: {
        ...mapGetters({
            access_token: 'common/access_token',
            areas: 'registration_lists/country_areas',
            reward_levels: 'registration_lists/reward_levels',
            profiles: 'registration_lists/profiles',
			tags: 'registration_lists/tags',
			clients: 'params/clients',
            id_user_client_selected: 'common/id_user_client_selected',
            num_rows: 'datatable/num_rows',
            id_user_language_selected: 'common/id_user_language_selected',
            reward_campaigns: 'registration_lists/reward_campaigns'
        }),

        getHeader() {
            let headers = {}
            return headers = {
                headers: {
                    'content-type': 'application/json',
                    'authorization': 'Basic ' + btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
                    'authorization': 'Bearer ' + this.access_token
                }
            }
		},
    },

    methods: {
        request ({ pagination }) {
            this.loading = true
            this.server_pagination = pagination

            let order = pagination.descending ? '-' : ''
            let order_by = pagination.sortBy ? order + pagination.sortBy : ''

            let data = {
                "page_size": this.server_pagination.rowsPerPage,
                "page": this.server_pagination.page,
                "search": this.filter,
                "ordering": order_by
            }

            this.savePagination()

            this.$axios.get('/admin_rewards/events_qrcode/?' + this.$qs.stringify(data), this.getHeader)
            .then(response => {
                this.server_pagination.rowsNumber = response.data.count

                this.events = response.data.results.map(r => {
                    return {
                        ...r,
                        start_date: moment(r.start_date).tz(r.timezone ? r.timezone : 'Etc/GMT').format('DD/MM/YYYY HH:mm:ss'),
                        end_date: moment(r.end_date).tz(r.timezone ? r.timezone : 'Etc/GMT').format('DD/MM/YYYY HH:mm:ss'),
                        permission: {
                            areas: r.areas,
                            profiles: r.profiles,
                            reward_levels: r.reward_levels
                        },
                        actions: r.id
                    }
                })

                this.loading = false
            })
            .catch(e => {
                this.loading = false
                console.log(e)
            })
        },

        fetchLists() {
            this.$q.loading.show()

            this.$axios.get('/accounts/get_registration_lists/', this.getHeader)
            .then(response => {
                this.$store.dispatch('registration_lists/SET_LISTS', [response.data])
                this.$q.loading.hide()

                this.request({
                    pagination: this.server_pagination
                })
            })
            .catch(e => {
                this.$q.loading.hide()
                console.log(e)
            })
        },

        confirmRemove(id) {
            this.action_id = id
            this.confirm_remove = true
        },

        removeItem() {
            this.$q.loading.show()

            this.$axios.delete(`/admin_rewards/events_qrcode/${this.action_id}/`, this.getHeader)
            .then(response => {
                this.$q.loading.hide()

                this.request({
                    pagination: this.server_pagination
                })

                this.confirm_remove = false

                this.$q.notify({
                    message: this.$t('item_removed_success'),
                    type: 'positive',
                    icon: 'far fa-trash-alt'
                })
            })
            .catch(e => {
                this.$q.loading.hide()
                console.log(e)
            })
        },

        getQuantity(permission) {
            return permission.areas.length + permission.profiles.length + permission.reward_levels.length
        },

        getArea(area) {
            let obj = this.areas.find((obj) => { return obj.value === area })
            if (obj) return obj.label
            else return ''
        },

        getUserProfile(profile) {
            let obj = this.profiles.find((obj) => { return obj.value === profile })
            if (obj) return obj.label
            else return ''
        },

        getCampaign(campaign) {
            let obj = this.reward_campaigns.find((obj) => { return obj.id === campaign })
            if (obj) return obj.title
            else return ''
        },

        getRewardLevels(value) {
            let obj = this.reward_levels.find((obj) => { return obj.value === value })
            if (obj) return obj.label
            else return ''
        },

        getStatus(status) {
            if (status == 1) return 'published'
            else if (status == 2) return 'unpublished'
            else if (status == 4) return 'finished'
        },

        getIconStatus(status) {
            if (status == 1) return 'fas fa-check'
            else if (status == 2) return 'fas fa-ban'
            else if (status == 4) return 'fal fa-check'
        },

        getColorStatus(status) {
            if (status == 1) return 'positive'
            else if (status == 2) return 'negative'
            else if (status == 4) return 'info'
        },

        savePagination() {
            this.$store.dispatch('datatable/SET_NUM_ROWS', [this.server_pagination.rowsPerPage])
        },

        loadPagination() {
            this.server_pagination.rowsPerPage = this.num_rows
        },

        fDate(d) {
            if (d) {
                let format = ''

                switch(this.id_user_language_selected) {
                    case 1:
                    case 3: format = 'DD/MM/YYYY HH:mm'
                    break
                    default: format = 'MM-DD-YYYY HH:mm'
                    break
                }

                return date.formatDate(d, format)
            }
        },
    },

    created() {
      this.loadPagination()
      this.fetchLists()
    }
}
</script>

<style>

</style>